import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Text } from '../../../shared/uiComponents';
import { ChevronLeft, ChevronRight } from '../../../shared/Assets/svgIcons';
import { TEXT } from '../../../shared/Helpers/colors';
import { months, weeks } from '../Helpers/constants';
import { DayProperties } from '../Helpers/interfaces';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	setDay as setDayGlobal,
	setEndOfWeek,
	setLastDayOfMonth as setLastDayOfMonthGlobal,
	setLastDayOfPreviousMonth as setLastDayOfPreviousMonthGlobal,
	setMonth as setMonthGlobal,
	setSelectedDay,
	setStartOfWeek,
	setWeekDay as setWeekDayGlobal,
	setYear as setYearGlobal,
} from '../../../redux/State/clientSlice/calendarHelperSlice';

const CalendarGadget = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const today = new Date();
	const currentDay = today.getDate();
	const currentMonth = today.getMonth();
	const currentYear = today.getFullYear();
	const weekDayOfFirstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
	const lastDayMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
	const lastDayPreviousMonth = new Date(currentYear, currentMonth, 0).getDate();

	const [month, setMonth] = useState<number>(currentMonth);
	const [weekDay, setWeekDay] = useState<number>(weekDayOfFirstDayOfMonth);
	const [lastDayOfMonth, setLastDayOfMonth] = useState<number>(lastDayMonth);
	const [lastDayOfPreviousMonth, setLastDayOfPreviousMonth] = useState<number>(lastDayPreviousMonth);
	const [year, setYear] = useState<number>(currentYear);
	const [days, setDays] = useState<DayProperties[]>([]);

	const selectedDay = useSelector((state) => state.calendarHelper.selectedDay);

	const actionHandler = (selectedMonth: number, selectedYear: number) => {
		setMonth(selectedMonth);
		setYear(selectedYear);

		const lastDayOfMonthValue = new Date(selectedYear, selectedMonth + 1, 0).getDate();
		setLastDayOfMonth(lastDayOfMonthValue);

		const lastDayOfPreviousMonthValue = new Date(selectedYear, selectedMonth, 0).getDate();
		setLastDayOfPreviousMonth(lastDayOfPreviousMonthValue);

		const nextWeekDay = new Date(selectedYear, selectedMonth, 1).getDay();
		setWeekDay(nextWeekDay);
	};

	const nextHandler = () => {
		if (month === 11) {
			const nextYear = year + 1;

			actionHandler(0, nextYear);

			return;
		}

		const nextMonth = month + 1;
		actionHandler(nextMonth, year);
	};

	const prevHandler = () => {
		if (month === 0) {
			const previousYear = year - 1;

			actionHandler(11, previousYear);

			return;
		}

		const prevMonth = month - 1;
		actionHandler(prevMonth, year);
	};

	useEffect(() => {
		const previousMonthDays: DayProperties[] = [];

		for (let i = weekDay; i > 0; i -= 1) {
			previousMonthDays.push({
				day: lastDayOfPreviousMonth - i + 1,
				status: 0,
				month: month - 1 < 0 ? 11 : month - 1,
				year: month - 1 < 0 ? year - 1 : year,
			});
		}

		const currentMonthDays: DayProperties[] = [];

		for (let i = 1; i <= lastDayOfMonth; i += 1) {
			currentMonthDays.push({
				day: i,
				status: 1,
				month,
				year,
			});
		}

		const nextMonthDays: DayProperties[] = [];
		const lastWeekDay = new Date(year, month + 1, 0).getDay();

		for (let i = 1; i < 7 - lastWeekDay; i += 1) {
			nextMonthDays.push({
				day: i,
				status: 2,
				month: month + 1 < 12 ? month + 1 : 0,
				year: month + 1 < 12 ? year : year + 1,
			});
		}
		setDays([...previousMonthDays, ...currentMonthDays, ...nextMonthDays]);
	}, [weekDay, lastDayOfMonth, year, month, lastDayOfPreviousMonth, dispatch]);

	const selectDayHandler = (item: DayProperties) => {
		dispatch(setSelectedDay(item));

		const clickedDay = item.day;
		const clickedMonth = item.month;
		const clickedYear = item.year;

		// Update local state to the clicked day's month/year
		setMonth(clickedMonth);
		setYear(clickedYear);

		// Update global state - this is what actually navigates the main calendar
		dispatch(setDayGlobal(clickedDay));
		dispatch(setMonthGlobal(clickedMonth));
		dispatch(setYearGlobal(clickedYear));

		const lastDayOfMonthValue = new Date(clickedYear, clickedMonth + 1, 0).getDate();
		setLastDayOfMonth(lastDayOfMonthValue);
		dispatch(setLastDayOfMonthGlobal(lastDayOfMonthValue));

		const lastDayOfLastMonth = new Date(clickedYear, clickedMonth, 0).getDate();
		setLastDayOfPreviousMonth(lastDayOfLastMonth);
		dispatch(setLastDayOfPreviousMonthGlobal(lastDayOfLastMonth));

		const nextWeekDay = new Date(clickedYear, clickedMonth, 1).getDay();
		setWeekDay(nextWeekDay);
		dispatch(setWeekDayGlobal(nextWeekDay));

		// Calculate which week contains the selected day for week view
		const selectedDate = new Date(clickedYear, clickedMonth, clickedDay);
		const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

		// Find the start of the week (Sunday)
		const startOfWeekDate = new Date(selectedDate);
		startOfWeekDate.setDate(selectedDate.getDate() - dayOfWeek);

		// Calculate the position in the calendar grid
		const firstDayOfMonth = new Date(clickedYear, clickedMonth, 1);
		const firstDayOfWeek = firstDayOfMonth.getDay();
		const daysFromFirstDay = clickedDay - 1;
		const totalDaysFromStart = firstDayOfWeek + daysFromFirstDay;

		// Calculate week position in the calendar grid
		const weekStartIndex = Math.floor(totalDaysFromStart / 7) * 7;
		const weekEndIndex = weekStartIndex + 7;

		// Update week view state
		dispatch(setStartOfWeek(weekStartIndex));
		dispatch(setEndOfWeek(weekEndIndex));
	};

	return (
		<div className="datePicker">
			<div className="dateHeader">
				<div className="icon" onClick={prevHandler}>
					<ChevronLeft />
				</div>
				<div className="text">
					<Text title={months[month]} type="h6" fontWeight="semiBold" />
					<Text title={year} type="h6" color={TEXT.TERTIARY} />
				</div>
				<div className="icon" onClick={nextHandler}>
					<ChevronRight />
				</div>
			</div>
			<div className="dateBody">
				<div className="dateBodyRow">
					{weeks.map((day, index) => (
						<Text key={index} title={day.slice(0, 2)} type="psm" fontWeight="medium" className="dateBodyWeekdayCell" />
					))}
					{days.map((item, index) => {
						const isToday = currentDay === item.day && currentMonth === item.month;
						const isSelected = selectedDay === item;

						return (
							<Text
								key={index}
								className={`dateBodyCell ${isSelected ? 'dataBodySelected' : ''} ${isToday ? 'dataBodyToday' : ''}`}
								onClick={() => selectDayHandler(item)}
								title={item.day.toString()}
								color={item.status === 1 ? TEXT.SECONDARY : TEXT.QUATERNARY}
								type="psm"
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default CalendarGadget;
